/**
 * draftSlice — draft save metadata + cross-slice hydration entry point.
 *
 * Round 1 scope: slice state + actions only. The auto-save subscription
 * logic (debounced persistence) lives in Module 3a (Round 2).
 *
 * Other slices set `draft.dirty = true` on state-mutating actions. The
 * subscription layer will watch `draft.dirty` and call `markSaving` ->
 * persist -> `markSaved`. Errors surface via `draft.saveStatus: 'error'`.
 *
 * Shape note: the slice's state lives under a nested `draft` object so
 * other slices can shallow-merge `{ draft: { ...state.draft, dirty: true } }`
 * without colliding with names like `dirty` that could appear elsewhere.
 * Actions are flat on the store root (standard Zustand pattern).
 */

import type { StateCreator } from 'zustand'

import type {
  CartItem,
  DraftId,
  DraftSaveStatus,
  OrderDraftDTO
} from 'models/purchaseFlowRevamp'
import type { CartAttachment } from './cartAttachmentsSlice'
import { clearStaleMeetingDate } from '../staleMeetingDate'
import { withoutFastDelivery } from '../serializeDraft'
import type { PfStoreState } from '../store'
import { withKnownCountry } from './propertySlice'

export interface DraftMeta {
  draftId?: DraftId
  /** True when local state has changed since the last successful save. */
  dirty: boolean
  /** Unix-ms timestamp of the last successful save. */
  lastSavedAt?: number
  saveStatus: DraftSaveStatus
  /** Last save error message (surface-only; ignored in v1 except logger). */
  saveError?: string
  /** True while `hydrateFromDraft` is populating slices. Auto-save must not
   *  fire during hydration — it would round-trip the same state as a "save". */
  isHydrating: boolean
  /** The order was placed: its draft is deleted and must not be saved again. Cleared when a new order starts. */
  isClosed: boolean
  /**
   * PF v2 purchase-session id. Established by `useEnsurePfV2Session` once a
   * property is selected (`POST /workspace/{ws}/sessions`). Flows onto the
   * catalogue request + the order placement body.
   *
   * - `null` until `useEnsurePfV2Session` successfully resolves.
   * - NOT persisted into `OrderDraftDTO` — regenerated on draft hydration.
   */
  sessionId: string | null
  /**
   * Workspace the current `sessionId` was opened in, stamped alongside it by
   * `setSessionId`.
   *
   * Exists because the v2 session-file endpoints are workspace-scoped
   * (`/workspace/{workspaceId}/sessions/{sessionId}/files/…`) and the backend binds
   * session→workspace before touching anything, answering 403 on a mismatch.
   * Re-deriving the workspace at upload time from `targetWorkspaceId ||
   * userPropertyWorkspaceId` can disagree with the session (an admin whose target
   * was cleared, a client whose property workspace resolves late), so it is captured
   * once, at the only moment it is known to be right.
   *
   * - `null` until `useEnsurePfV2Session` resolves; cleared with `sessionId`.
   * - NOT persisted into `OrderDraftDTO` — regenerated with the session on resume.
   */
  sessionWorkspaceId: string | null
  /**
   * Property the current `sessionId` was opened for. The backend places the order on the session's
   * property, so placement refuses when this differs from the property on screen.
   */
  sessionPropertyId: string | null
  /**
   * PF v2 session-create lifecycle status. Distinguishes the pre-session
   * window / in-flight create / success / failure so the catalogue can show
   * skeletons (`pending`) vs a real error+retry (`error`) instead of silently
   * falling back to the mock catalogue on a FAILED session.
   *
   * - `idle`    — no create attempted yet (no property, or before first fire).
   * - `pending` — create in flight.
   * - `ready`   — session created (`sessionId` set).
   * - `error`   — create failed; the catalogue must NOT show the mock.
   *
   * NOT persisted — reset on draft hydrate/reset (session regenerates on resume).
   */
  sessionStatus: SessionStatus
  /**
   * Whether a session already rejected by the backend has been discarded once
   * this visit (see `discardRejectedSession`). Bounds the recovery to a single
   * attempt: if the replacement session is refused too, the cause is not a stale
   * id and re-minting forever would only hide it.
   *
   * NOT persisted — a new visit deserves a fresh attempt.
   */
  sessionRecoveryAttempted: boolean
  /** The last create failed because the property cannot be ordered yet (missing address, location or area), which only completing the property fixes. */
  sessionPropertyIncomplete: boolean
}

/** PF v2 session-create lifecycle status. */
export type SessionStatus = 'idle' | 'pending' | 'ready' | 'error'

export interface DraftSliceState {
  draft: DraftMeta
}

export interface DraftSliceActions {
  markDirty(): void
  markSaving(): void
  markSaved(at?: number): void
  markError(message: string): void
  setDraftId(id: DraftId | undefined): void
  /** Guards the auto-save subscription while a draft is being restored. */
  setHydrating(flag: boolean): void
  closeDraft(): void
  /**
   * Stores the PF v2 session id together with the workspace it was opened in
   * (and flips status to `ready` when the id is non-null).
   *
   * `workspaceId` is a required parameter rather than an optional one on purpose:
   * the session-file endpoints cannot address a session without it, and an
   * overload that let a caller set the id alone would make "session established,
   * workspace unknown" representable — the exact state that 403s at upload time.
   */
  setSessionId(sessionId: string | null, workspaceId: string | null, propertyId?: string | null): void
  /** Sets the PF v2 session-create lifecycle status, and whether a failure was the property being incomplete. */
  setSessionStatus(status: SessionStatus, propertyIncomplete?: boolean): void
  /**
   * Resets the session lifecycle to `idle` so `useEnsurePfV2Session` re-fires the
   * create (R1 Retry from the catalogue error state). Only acts when currently
   * `error` — a no-op otherwise so it can't disrupt an in-flight/ready session.
   */
  retrySession(): void
  /**
   * Throws away a session the backend refuses to serve, so the ensure-hook mints
   * a replacement. Once per visit — the second refusal is reported, not retried.
   * Returns true when it acted.
   */
  discardRejectedSession(): boolean
  /** Replaces the entire store state from a restored draft DTO. */
  hydrateFromDraft(dto: OrderDraftDTO): void
  /**
   * Starts fresh: every field returns to the value the store was created with, except the few kept
   * across orders (`KEPT_ON_RESET` in `store.ts`). Defined by the store, which is the only place that
   * sees every slice. Silent: it does not mark the draft dirty.
   */
  resetPersistedState(): void
}

export type DraftSlice = DraftSliceState & DraftSliceActions

export const initialDraftMeta: DraftMeta = {
  draftId: undefined,
  dirty: false,
  lastSavedAt: undefined,
  saveStatus: 'idle',
  saveError: undefined,
  isHydrating: false,
  isClosed: false,
  sessionId: null,
  sessionWorkspaceId: null,
  sessionPropertyId: null,
  sessionStatus: 'idle',
  sessionRecoveryAttempted: false,
  sessionPropertyIncomplete: false,
}


/**
 * Persisted attachment expectations → the in-store shape. The signed URL is not
 * persisted (short-lived), so a resumed file has none until something re-signs it;
 * the listing UI already tolerates a null URL.
 */
function hydrateAttachments(
  persisted: OrderDraftDTO['attachments']
): Record<string, CartAttachment[]> {
  if (!persisted) return {}
  const out: Record<string, CartAttachment[]> = {}
  for (const [cartItemId, files] of Object.entries(persisted)) {
    out[cartItemId] = files.map((file) => ({
      kind: 'staging-visual' as const,
      gcFilename: file.gcFilename,
      originalFilename: file.originalFilename,
      signedUrl: null,
    }))
  }
  return out
}

/** A draft saved before the pack's id was renamed carries `instanceId`; read it as `selectionId`. */
const withPackSelectionId = (item: CartItem): CartItem => {
  const pack = item.pack as (CartItem['pack'] & { instanceId?: string }) | undefined
  if (!pack || pack.selectionId || !pack.instanceId) return item
  return { ...item, pack: { productCode: pack.productCode, selectionId: pack.instanceId } }
}

export const createDraftSlice: StateCreator<PfStoreState, [], [], Omit<DraftSlice, 'resetPersistedState'>> = (set, get) => ({
  draft: initialDraftMeta,

  markDirty: () => set((state) => (
    state.draft.dirty ? state : { draft: { ...state.draft, dirty: true } }
  )),

  markSaving: () => set((state) => ({
    draft: { ...state.draft, saveStatus: 'saving', saveError: undefined },
  })),

  markSaved: (at = Date.now()) => set((state) => ({
    draft: {
      ...state.draft,
      saveStatus: 'saved',
      dirty: false,
      lastSavedAt: at,
      saveError: undefined,
    },
  })),

  markError: (message) => set((state) => ({
    draft: { ...state.draft, saveStatus: 'error', saveError: message },
  })),

  setDraftId: (draftId) => set((state) => ({
    draft: { ...state.draft, draftId },
  })),

  closeDraft: () => set((state) => (state.draft.isClosed ? state : { draft: { ...state.draft, isClosed: true } })),

  setHydrating: (flag) => set((state) => (
    state.draft.isHydrating === flag ? state : { draft: { ...state.draft, isHydrating: flag } }
  )),

  setSessionId: (sessionId, workspaceId, propertyId = null) => set((state) => {
    // Setting a non-null id implies the session is ready; clearing it does not
    // by itself set an error (callers use setSessionStatus for that).
    const nextStatus = sessionId ? 'ready' : state.draft.sessionStatus
    // Clearing the id clears what it was opened for with it — left behind, they
    // would outlive the session they describe and address the next one wrongly.
    const nextWorkspaceId = sessionId ? workspaceId : null
    const nextPropertyId = sessionId ? propertyId : null
    if (
      state.draft.sessionId === sessionId &&
      state.draft.sessionWorkspaceId === nextWorkspaceId &&
      state.draft.sessionPropertyId === nextPropertyId &&
      state.draft.sessionStatus === nextStatus
    ) return state
    return {
      draft: { ...state.draft, sessionId, sessionWorkspaceId: nextWorkspaceId, sessionPropertyId: nextPropertyId, sessionStatus: nextStatus },
    }
  }),

  setSessionStatus: (sessionStatus, propertyIncomplete = false) => set((state) => (
    state.draft.sessionStatus === sessionStatus && state.draft.sessionPropertyIncomplete === propertyIncomplete
      ? state
      : { draft: { ...state.draft, sessionStatus, sessionPropertyIncomplete: propertyIncomplete } }
  )),

  retrySession: () => set((state) => (
    state.draft.sessionStatus === 'error'
      ? { draft: { ...state.draft, sessionStatus: 'idle', sessionPropertyIncomplete: false } }
      : state
  )),

  discardRejectedSession: () => {
    const { draft } = get()
    if (!draft.sessionId || draft.sessionRecoveryAttempted) return false
    set((state) => ({
      draft: {
        ...state.draft,
        sessionId: null,
        sessionWorkspaceId: null,
        sessionPropertyId: null,
        sessionStatus: 'idle',
        sessionRecoveryAttempted: true,
      },
    }))
    return true
  },

  hydrateFromDraft: (dto) => set((state) => {
    const items: CartItem[] = (dto.items ?? []).map(withPackSelectionId)
    return {
      // property
      // A draft saved before the unknown-country fix may still carry XX.
      property: dto.property ? withKnownCountry(dto.property) : null,
      // admin targets (Module 4)
      targetUserId: dto.targetUserId ?? null,
      targetWorkspaceId: dto.workspaceId ?? null,
      // Restored, not re-asked: the admin gate opens only when BOTH the target
      // workspace and the target email are known, so leaving this null sent every
      // resumed admin draft back to client selection — which then re-picked the
      // workspace too. Older drafts carry no email and still land on the gate.
      targetUserEmail: dto.targetClientEmail ?? null,
      adminSkipCtAutomation: dto.adminSkips?.ctAutomation ?? false,
      adminSkipExternalProviderSending: dto.adminSkips?.externalProviderSending ?? false,
      // cart
      items,
      // Files each line EXPECTS to still be on the session. The resume path
      // verifies them against the session's own listing and prunes what expired,
      // so hydrating them here is a starting point, not a claim.
      attachments: hydrateAttachments(dto.attachments),
      uploadDisplayNames: dto.uploadDisplayNames ?? {},
      // per-item configs
      productConfigs: dto.configs ?? {},
      // Per-(cartItem, flow) user-confirmed flag (Phase C-A). Default `{}` for
      // legacy drafts — the tab confirms once the client opens it after resuming.
      cartItemUserConfirmed: dto.configsUserConfirmed ?? {},
      // instructions
      // A draft can outlive the slot it booked — a date already in the past is
      // dropped so the client re-picks, rather than silently ordering it.
      meeting: clearStaleMeetingDate(dto.instructions.meeting),
      ctBookingId: dto.instructions.ctBookingId,
      ctBookingName: dto.instructions.ctBookingName,
      extraServices: withoutFastDelivery(dto.instructions.extraServices ?? []),
      comments: dto.instructions.comments,
      scheduledByCT: dto.instructions.scheduledByCT,
      scheduleMethod: dto.instructions.scheduleMethod,
      // billing
      invoicingAddress: dto.billing.invoicingAddress,
      paymentMethod: dto.billing.paymentMethod,
      externalReporting: dto.billing.externalReporting,
      reference: dto.billing.reference,
      propertyOwner: dto.billing.propertyOwner,
      ccEmails: dto.billing.ccEmails ?? [],
      // draft meta — hydration itself is not a dirty mutation
      draft: {
        ...state.draft,
        draftId: dto.draftId,
        dirty: false,
        isClosed: false,
        saveStatus: 'saved',
        lastSavedAt: Date.parse(dto.updatedAt) || state.draft.lastSavedAt,
        saveError: undefined,
        // The session IS persisted now (uploads are session-scoped, so a fresh
        // session cannot see or use anything uploaded before). A draft written
        // before that, or saved before a session existed, still resumes the old
        // way: no session, and `useEnsurePfV2Session` mints one.
        sessionId: dto.session?.id ?? null,
        sessionWorkspaceId: dto.session?.workspaceId ?? null,
        // Saved together with the property, so the session was opened for it.
        sessionPropertyId: dto.session?.id ? dto.property?.id ?? null : null,
        sessionStatus: dto.session?.id ? 'ready' : 'idle',
      },
    }
  }),

})
